import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  User,
  Location,
  Commodity,
  StationPrices,
  WholesalePrice,
  Tank,
  MarginCalculation,
  Industry,
  ChatMessage,
} from '@jrd/core';

// ============================================================================
// AUTH STORE
// ============================================================================

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      setUser: (user) => set({ user, isAuthenticated: !!user, isLoading: false }),
      setToken: (token) => set({ token }),
      logout: () => set({ user: null, token: null, isAuthenticated: false }),
    }),
    {
      name: 'jrd-auth',
      partialize: (state) => ({ token: state.token }),
    }
  )
);

// ============================================================================
// APP STORE
// ============================================================================

interface AppState {
  // Boot state
  hasBooted: boolean;
  setHasBooted: (value: boolean) => void;

  // Current location
  currentLocationId: string | null;
  setCurrentLocationId: (id: string | null) => void;

  // Locations
  locations: Location[];
  setLocations: (locations: Location[]) => void;

  // Active module
  activeModule: 'price-o-tron' | 'docutron' | 'jack2' | 'aut0tron' | 'scan-o-tron';
  setActiveModule: (module: AppState['activeModule']) => void;

  // Active industry view
  activeIndustry: Industry;
  setActiveIndustry: (industry: Industry) => void;

  // Sidebar collapsed
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;

  // Jack2 chat open
  jack2Open: boolean;
  setJack2Open: (open: boolean) => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      hasBooted: false,
      setHasBooted: (hasBooted) => set({ hasBooted }),

      currentLocationId: null,
      setCurrentLocationId: (currentLocationId) => set({ currentLocationId }),

      locations: [],
      setLocations: (locations) => set({ locations }),

      activeModule: 'price-o-tron',
      setActiveModule: (activeModule) => set({ activeModule }),

      activeIndustry: 'gas',
      setActiveIndustry: (activeIndustry) => set({ activeIndustry }),

      sidebarCollapsed: false,
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

      jack2Open: false,
      setJack2Open: (jack2Open) => set({ jack2Open }),
    }),
    {
      name: 'jrd-app',
      partialize: (state) => ({
        currentLocationId: state.currentLocationId,
        activeModule: state.activeModule,
        activeIndustry: state.activeIndustry,
        sidebarCollapsed: state.sidebarCollapsed,
      }),
    }
  )
);

// ============================================================================
// PRICING STORE (Price-O-Tron)
// ============================================================================

export interface EmailConfig {
  email: string;
  folder: string;
  subjectFilter: string;
  autoProcess: boolean;
  lastChecked?: Date;
}

export interface GasBuddyCompetitor {
  id: string;
  name: string;
  address: string;
  distance?: number;
  lat: number;
  lng: number;
  prices: {
    grade: '87' | '89' | '91';
    price: number;
    postedTime: string;
  }[];
}

interface PricingState {
  // Commodities
  commodities: Commodity[];
  setCommodities: (commodities: Commodity[]) => void;
  commoditiesLastUpdated: Date | null;

  // Station prices
  stationPrices: Record<string, StationPrices>;
  setStationPrices: (stationId: string, prices: StationPrices) => void;

  // Competitors (from GasBuddy)
  competitors: GasBuddyCompetitor[];
  setCompetitors: (competitors: GasBuddyCompetitor[]) => void;
  competitorsLastUpdated: Date | null;

  // Wholesale (from price notice emails)
  wholesalePrices: Record<string, WholesalePrice>;
  setWholesalePrice: (locationId: string, price: WholesalePrice) => void;

  // Margins (calculated: retail - wholesale)
  margins: Record<string, MarginCalculation>;
  setMargin: (locationId: string, margin: MarginCalculation) => void;

  // Tanks
  tanks: Record<string, Tank[]>;
  setTanks: (locationId: string, tanks: Tank[]) => void;

  // Email configuration for price notices
  emailConfigs: Record<string, EmailConfig>;
  setEmailConfig: (locationId: string, config: EmailConfig) => void;

  // Loading states
  isLoadingPrices: boolean;
  setIsLoadingPrices: (loading: boolean) => void;
  isLoadingCompetitors: boolean;
  setIsLoadingCompetitors: (loading: boolean) => void;

  // Last refresh timestamp
  pricesLastUpdated: Date | null;
}

export const usePricingStore = create<PricingState>()(
  persist(
    (set) => ({
      commodities: [],
      setCommodities: (commodities) => set({ commodities, commoditiesLastUpdated: new Date() }),
      commoditiesLastUpdated: null,

      stationPrices: {},
      setStationPrices: (stationId, prices) =>
        set((s) => ({
          stationPrices: { ...s.stationPrices, [stationId]: prices },
          pricesLastUpdated: new Date(),
        })),

      competitors: [],
      setCompetitors: (competitors) =>
        set({ competitors, competitorsLastUpdated: new Date() }),
      competitorsLastUpdated: null,

      wholesalePrices: {},
      setWholesalePrice: (locationId, price) =>
        set((s) => ({ wholesalePrices: { ...s.wholesalePrices, [locationId]: price } })),

      margins: {},
      setMargin: (locationId, margin) =>
        set((s) => ({ margins: { ...s.margins, [locationId]: margin } })),

      tanks: {},
      setTanks: (locationId, tanks) => set((s) => ({ tanks: { ...s.tanks, [locationId]: tanks } })),

      emailConfigs: {},
      setEmailConfig: (locationId, config) =>
        set((s) => ({ emailConfigs: { ...s.emailConfigs, [locationId]: config } })),

      isLoadingPrices: false,
      setIsLoadingPrices: (isLoadingPrices) => set({ isLoadingPrices }),
      isLoadingCompetitors: false,
      setIsLoadingCompetitors: (isLoadingCompetitors) => set({ isLoadingCompetitors }),

      pricesLastUpdated: null,
    }),
    {
      name: 'jrd-pricing',
      partialize: (state) => ({
        wholesalePrices: state.wholesalePrices,
        emailConfigs: state.emailConfigs,
      }),
    }
  )
);

// ============================================================================
// CHAT STORE (Jack2)
// ============================================================================

interface ChatState {
  messages: ChatMessage[];
  addMessage: (message: ChatMessage) => void;
  clearMessages: () => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

export const useChatStore = create<ChatState>()((set) => ({
  messages: [],
  addMessage: (message) => set((s) => ({ messages: [...s.messages, message] })),
  clearMessages: () => set({ messages: [] }),
  isLoading: false,
  setIsLoading: (isLoading) => set({ isLoading }),
}));
